import React, { useEffect, useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { Divider, Link, Menu, MenuItem } from '@material-ui/core';
import { PasslyButton } from '@app/components';
import Icon from '@app/components/Icon';
import { DATE_FORMAT, localStore, passlyFormatDate, RELEASE_DATE_JSON_URL, WHAT_NEW_BUTTON_URL } from '@app/helpers';
import fetch from 'isomorphic-fetch';
import { HELP_MENU } from '@app/pages/ssoManager/addEditApplication.enums';
import { PasslyTooltip } from '@app/componentsV2';
import {useDispatch} from 'react-redux';
import {showInAppVideos} from '@app/store/actions';

interface IProps {
    appVersion: string;
}

export const HelpMenu: React.SFC<IProps> = ({ appVersion }) => {
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [releaseDate, setReleaseDate] = React.useState('');
    const [appVersionOld, setAppVersionOld] = useState({ appVersion: '', isWhatNewSeen: false, isMenuUpdateSeen: false });

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        const versionString = localStore.getItem('appVersion');
        const version = versionString ? JSON.parse(versionString) : null;
        const newAppVersionState = { ...version, isMenuUpdateSeen: true };
        const itemToSet = JSON.stringify(newAppVersionState);
        setAnchorEl(event.currentTarget);

        localStore.setItem('appVersion', itemToSet);
        setAppVersionOld(newAppVersionState);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onClick = () => {
        const newAppVersionState = { appVersion: appVersion, isWhatNewSeen: true, isMenuUpdateSeen: true };
        const itemToSet = JSON.stringify(newAppVersionState);

        localStore.setItem('appVersion', itemToSet);
        setAppVersionOld(newAppVersionState);
        window.open(WHAT_NEW_BUTTON_URL, '_blank');
    };

    const openInAppVideosPopup = () => {
        handleClose();
        dispatch(showInAppVideos());
    };

    const getMenuItems = () => {
        return HELP_MENU.map((item, index) => {
            switch (item.type) {
                case 'help-text':
                    return <div key={index} className='help-menu__help-text'>{translation(item.label)}</div>;
                case 'item':
                    return (
                        <MenuItem key={index} className='help-menu__row'>
                            <a href={item.url} className='help-menu__menu-element' target='_blank' rel='noopener noreferrer'>
                                {translation(item.label)}
                            </a>
                        </MenuItem>
                    );
                case 'in-app-videos':
                    return (
                        <MenuItem key={index} className='help-menu__row'>
                            <Link onClick={openInAppVideosPopup} underline='none' className='help-menu__menu-element'>
                                {translation(item.label)}
                            </Link>
                        </MenuItem>
                    );
                case 'divider':
                    return <Divider key={index} className='help-menu__divider' />;
                default:
                    return null;
            }
        });
    };

    const getMenuFooter = () => {
        return (
            <div className='help-menu__footer'>
                <div className='help-menu__version'>
                    {!appVersionOld.isWhatNewSeen && <span className='help-menu__version-dot' />}v{appVersion + ' - ' + releaseDate}
                </div>
                <PasslyButton className='help-menu__version-edit-button' variant='contained' color='primary' onClick={onClick}>
                    {translation('HELP_MENU_BUTTON')}
                </PasslyButton>
            </div>
        );
    };

    useEffect(() => {
        const versionString = localStore.getItem('appVersion');
        const version = versionString ? JSON.parse(versionString) : null;

        if (!version) {
            const itemToSet = JSON.stringify({ appVersion: appVersion, isWhatNewSeen: false, isMenuUpdateSeen: false });

            localStore.setItem('appVersion', itemToSet);
        } else if (version.isWhatNewSeen && version.appVersion === appVersion) {
            setAppVersionOld({ ...version, isWhatNewSeen: version.isWhatNewSeen, isMenuUpdateSeen: version.isMenuUpdateSeen });
        } else {
            const newAppVersionState = { appVersion: appVersion, isWhatNewSeen: false, isMenuUpdateSeen: version.isMenuUpdateSeen };

            setAppVersionOld(newAppVersionState);
        }

        fetch(RELEASE_DATE_JSON_URL)
            .then((response) => {
                return response.json();
            })
            .then((data) => {
                const dateToFormat = new Date(data.modified_at * 1000);

                setReleaseDate(passlyFormatDate(dateToFormat, DATE_FORMAT));
            });
    }, []);

    return (
        <>
            <PasslyTooltip title={translation('NAVBAR_HELP_TOOLTIP')} placement={'bottom'}>
                <span>
                    <PasslyButton id='profileIconButton' className={`help-menu__trigger ${anchorEl ? 'navbar__content-active' : ''}`} onClick={handleClick}>
                        <Icon icon='helpIco' className='navbar__icon' />
                        {!appVersionOld.isMenuUpdateSeen ? <div className='help-menu__red-notification' /> : null}
                    </PasslyButton>
                </span>
            </PasslyTooltip>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                className='help-menu'
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                getContentAnchorEl={null}
            >
                {getMenuItems()}
                {getMenuFooter()}
            </Menu>
        </>
    );
};
